import { useEffect, useMemo, useRef, useState } from 'react';
import { audioContext } from '@/audio/context';
import { recordedSheets } from '@/audio/recorded';
import type { SheetManifest } from '@/audio/sheets';
import { SheetVoice, WebVoice } from '@/audio/voice';
import { cx } from '@/lib/cx';
import s from './debug.module.css';
import { Card } from './ui';

const MANIFEST = `${import.meta.env.BASE_URL}voice/manifest.json`;

interface Clip {
  text: string;
  sheet: string;
  seconds: number;
  /** Spliced from more than one range (a template, or a spliced r). */
  pieces: number;
  template: boolean;
}

/**
 * The recorded voice (public/voice): every line and word in the sheets, searchable and playable
 * as the game plays it, templates filled with a chosen word.
 */
export function VoicePanel() {
  const [manifest, setManifest] = useState<SheetManifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [fill, setFill] = useState('rabbit');
  const [playing, setPlaying] = useState<string | null>(null);
  const voice = useRef<SheetVoice | null>(null);

  useEffect(() => {
    fetch(MANIFEST)
      .then((r) =>
        r.ok ? (r.json() as Promise<SheetManifest>) : Promise.reject(new Error(r.statusText)),
      )
      .then(setManifest)
      .catch((e: unknown) => {
        setError(String(e));
      });
    return () => voice.current?.stop();
  }, []);

  const clips = useMemo<Clip[]>(
    () =>
      manifest?.sheets.flatMap((sheet) =>
        Object.entries(sheet.clips).map(([text, ranges]) => ({
          text,
          sheet: sheet.file,
          seconds: ranges.reduce((sum, r) => sum + r.end - r.start, 0),
          pieces: ranges.length,
          template: /\{(word|sound|heard)\}/.test(text),
        })),
      ) ?? [],
    [manifest],
  );
  const q = query.trim().toLowerCase();
  const shown = q ? clips.filter((c) => c.text.toLowerCase().includes(q)) : clips;

  const play = async (clip: Clip) => {
    const text = clip.text.replace(/\{(word|sound|heard)\}/g, fill || 'rabbit');
    voice.current ??= new SheetVoice(audioContext, new WebVoice(audioContext), recordedSheets());
    // The library resolves on the voice's first turn; wait for it so the recording plays.
    await recordedSheets();
    setPlaying(clip.text);
    await voice.current.play([{ kind: 'say', text }]);
    setPlaying((p) => (p === clip.text ? null : p));
  };

  return (
    <>
      <Card title="Recorded voice">
        {error ? (
          <p className={s.hint}>Could not load the manifest: {error}</p>
        ) : !manifest ? (
          <p className={s.hint}>Loading…</p>
        ) : (
          <dl className={s.kv}>
            <dt>Voice</dt>
            <dd>{manifest.voice}</dd>
            {manifest.sheets.map((sheet) => (
              <FragmentRow key={sheet.file} label={sheet.file}>
                {Object.keys(sheet.clips).length} clips
              </FragmentRow>
            ))}
          </dl>
        )}
      </Card>

      <Card
        title={`Lines (${String(shown.length)}${q ? ` of ${String(clips.length)}` : ''})`}
        actions={
          <div className={s.row}>
            <input
              className={s.input}
              type="search"
              placeholder="Search lines"
              aria-label="Search lines"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
            />
            <input
              className={s.input}
              aria-label="Word for {word} in templates"
              title="Fills {word}, {sound} and {heard} in templates"
              value={fill}
              onChange={(e) => {
                setFill(e.target.value);
              }}
              style={{ width: 100 }}
            />
            <button
              type="button"
              className={s.btn}
              disabled={!playing}
              onClick={() => {
                voice.current?.stop();
                setPlaying(null);
              }}
            >
              Stop
            </button>
          </div>
        }
        flush
      >
        <div className={s.tableWrap} style={{ maxHeight: '60dvh' }}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>
                  <span className={s.visuallyHidden}>Play</span>
                </th>
                <th>Line</th>
                <th className={s.num}>Sec</th>
                <th className={s.hideNarrow}>Sheet</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((clip) => (
                <tr
                  key={`${clip.sheet}/${clip.text}`}
                  className={cx(playing === clip.text && s.current)}
                >
                  <td style={{ width: 1 }}>
                    <button
                      type="button"
                      className={cx(s.btn, s.small)}
                      aria-label={`Play: ${clip.text}`}
                      onClick={() => {
                        void play(clip);
                      }}
                    >
                      ▶
                    </button>
                  </td>
                  <td>
                    {clip.text}{' '}
                    {clip.template && <span className={cx(s.badge, s.badgeAccent)}>template</span>}{' '}
                    {!clip.template && clip.pieces > 1 && (
                      <span className={cx(s.badge, s.badgeWarn)}>{clip.pieces} pieces</span>
                    )}
                  </td>
                  <td className={cx(s.num, s.mono)}>{clip.seconds.toFixed(1)}</td>
                  <td className={cx(s.hideNarrow, s.mono)}>{clip.sheet.split('-')[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {manifest && shown.length === 0 && <p className={s.empty}>No line matches.</p>}
        </div>
      </Card>
    </>
  );
}

function FragmentRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}
